// Client-side tool executor. The backend agent decides *which* tool to call;
// this module runs it against the live Office document (Office.js is only
// available in the add-in). Tool names MUST stay in sync with the CLIENT_TOOLS
// registry in server/agent.mjs.
//
// Each executor validates inputs defensively and returns a structured result
// the backend feeds back to the LLM as a `tool` message. Errors never throw —
// they come back as { error } so the agent can reason about failures.

import { ChartType, HostKind } from "./types";
import * as Word from "../host/word";
import * as Excel from "../host/excel";
import * as Ppt from "../host/powerpoint";

export interface ClientToolResult {
  ok: boolean;
  /** Human-readable summary for the agent + trace UI. */
  summary: string;
  /** Optional structured payload (e.g. data read from the document). */
  data?: unknown;
  error?: string;
}

function fail(message: string): ClientToolResult {
  return { ok: false, summary: message, error: message };
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x ?? "")) : [];
}

function asTable(v: unknown): unknown[][] | null {
  if (!Array.isArray(v)) return null;
  const out: unknown[][] = [];
  for (const row of v) {
    if (!Array.isArray(row)) return null;
    out.push(row);
  }
  return out;
}

// ---- Word ----
const MAX_DOC_CHARS = 12000; // prevent context overflow for very large documents
async function wordGetSelection(): Promise<ClientToolResult> {
  const text = await Word.getSelectedText();
  return { ok: true, summary: text.trim() ? `读取到选区（${text.length} 字）` : "选区为空", data: { text } };
}
async function wordGetDocument(): Promise<ClientToolResult> {
  const text = await Word.getDocumentText();
  const total = text.length;
  const truncated = total > MAX_DOC_CHARS ? text.slice(0, MAX_DOC_CHARS) : text;
  const summary = total > MAX_DOC_CHARS
    ? `全文共 ${total} 字，已截取前 ${MAX_DOC_CHARS} 字。如需完整内容处理长文档，请使用 word_get_selection 逐段操作。`
    : `读取到全文（${total} 字）`;
  return { ok: true, summary, data: { text: truncated, truncated: total > MAX_DOC_CHARS, totalChars: total } };
}
async function wordApplyEdits(args: Record<string, unknown>): Promise<ClientToolResult> {
  const ops = Array.isArray(args.ops) ? args.ops : null;
  if (!ops) return fail("缺少 ops 操作列表");
  const log = await Word.applyOps(ops as never);
  return { ok: true, summary: `执行 ${log.length} 项：${log.join("；")}`, data: { log } };
}
async function wordAddComment(args: Record<string, unknown>): Promise<ClientToolResult> {
  const comment = String(args.comment ?? "").trim();
  if (!comment) return fail("缺少批注内容");
  await Word.addComment(comment);
  return { ok: true, summary: "已对选区添加批注" };
}

// ---- Word: search-driven tools (let the LLM locate content without selection) ----
function toSearchOpts(args: Record<string, unknown>) {
  return {
    useRegExp: !!args.useRegExp,
    matchCase: !!args.matchCase,
    matchWholeWord: !!args.matchWholeWord,
  };
}
async function wordSearch(args: Record<string, unknown>): Promise<ClientToolResult> {
  const query = String(args.query ?? "").trim();
  if (!query) return fail("缺少 query 搜索词");
  const r = await Word.searchDocument(query, toSearchOpts(args));
  const shown = r.hits.length;
  const summary =
    r.total === 0
      ? `未匹配到「${query}」`
      : shown >= r.total
      ? `匹配 ${r.total} 处（第 ${r.hits[0]?.para} 段起）`
      : `共匹配 ${r.total} 处，本次返回前 ${shown} 处`;
  return { ok: true, summary, data: { query, hits: r.hits, total: r.total, truncated: shown < r.total } };
}
async function wordHighlight(args: Record<string, unknown>): Promise<ClientToolResult> {
  // Accept either a single `query` string or a `queries` array — the latter
  // lets the model highlight multiple related terms (e.g. ["AI","智能","数智化"]) in one shot.
  const queries = Array.isArray(args.queries)
    ? (args.queries as unknown[]).map((x) => String(x ?? "")).filter(Boolean)
    : [String(args.query ?? "").trim()].filter(Boolean);
  if (!queries.length) return fail("缺少 query/queries 关键词");
  const color = String(args.color || "#FFFF00");
  const opts = { ...toSearchOpts(args), bold: !!args.bold };
  let total = 0;
  const perQuery: { query: string; count: number }[] = [];
  for (const q of queries) {
    const n = await Word.highlightRanges(q, color, opts);
    perQuery.push({ query: q, count: n });
    total += n;
  }
  return {
    ok: true,
    summary: total ? `已高亮 ${total} 处（${queries.length} 个关键词）` : `未匹配到任何关键词，未做改动`,
    data: { color, total, perQuery },
  };
}
async function wordAddCommentAt(args: Record<string, unknown>): Promise<ClientToolResult> {
  const queries = Array.isArray(args.queries)
    ? (args.queries as unknown[]).map((x) => String(x ?? "")).filter(Boolean)
    : [String(args.query ?? "").trim()].filter(Boolean);
  const comment = String(args.comment ?? "").trim();
  if (!queries.length) return fail("缺少 query/queries 关键词");
  if (!comment) return fail("缺少 comment 批注内容");
  if (!Word.supportsComments()) return fail("当前 Word 版本不支持批注（需 WordApi 1.4+）");
  let total = 0;
  for (const q of queries) {
    total += await Word.addCommentToRanges(q, comment, toSearchOpts(args));
  }
  return {
    ok: total > 0,
    summary: total ? `已对 ${total} 处匹配文本添加批注` : `未匹配到任何关键词，未添加批注`,
    data: { total },
  };
}
async function wordGetHighlights(args: Record<string, unknown>): Promise<ClientToolResult> {
  const color = typeof args.color === "string" ? args.color : undefined;
  const r = await Word.getHighlights(color);
  const shown = r.spans.length;
  const summary =
    r.total === 0
      ? color ? `没有 ${color} 颜色的高亮` : "文档中没有任何高亮"
      : shown >= r.total
      ? `共 ${r.total} 处高亮（分布于 ${new Set(r.spans.map((s) => s.para)).size} 个段落）`
      : `共 ${r.total} 处高亮，本次返回前 ${shown} 处`;
  return { ok: true, summary, data: { spans: r.spans, total: r.total, truncated: shown < r.total } };
}
async function wordClearHighlights(args: Record<string, unknown>): Promise<ClientToolResult> {
  const query = typeof args.query === "string" ? args.query : undefined;
  const color = typeof args.color === "string" ? args.color : undefined;
  const n = await Word.clearHighlights(query, color);
  return {
    ok: true,
    summary: n ? `已清除 ${n} 处高亮` : "没有匹配的高亮可清除",
    data: { cleared: n },
  };
}
async function wordGetOutline(): Promise<ClientToolResult> {
  const r = await Word.getOutline();
  const summary = r.total ? `文档共 ${r.total} 个标题（大纲节点）` : "文档没有检测到标题/大纲层级";
  return { ok: true, summary, data: { outline: r.nodes, total: r.total } };
}
async function wordReplaceAll(args: Record<string, unknown>): Promise<ClientToolResult> {
  const query = String(args.query ?? "");
  const replacement = String(args.replacement ?? "");
  if (!query) return fail("缺少 query 查找内容");
  const n = await Word.replaceAll(query, replacement, toSearchOpts(args));
  return {
    ok: true,
    summary: n ? `已替换 ${n} 处「${query}」→「${replacement}」` : `未找到「${query}」，未做替换`,
    data: { replaced: n },
  };
}

// ---- Excel ----
const MAX_EXCEL_ROWS = 200; // sent in one batch; the Agent can request additional ranges
function sheetDataSummary(d: { headers: string[]; rows: unknown[][] }, shown: number): string {
  const total = d.rows.length;
  if (total <= shown) return `读取到 ${d.headers.length} 列 × ${total} 行`;
  return `共 ${d.headers.length} 列 × ${total} 行，本次返回前 ${shown} 行。如需剩余数据，请指定行范围再次调用 excel_get_sheet 并传 fromRow 参数`;
}
async function excelGetSelection(): Promise<ClientToolResult> {
  const d = await Excel.getSelectedData();
  const shown = Math.min(d.rows.length, MAX_EXCEL_ROWS);
  return { ok: true, summary: sheetDataSummary(d, shown), data: { sheetName: d.sheetName, address: d.address, headers: d.headers, rows: d.rows.slice(0, shown), totalRows: d.rows.length } };
}
async function excelGetSheet(args: Record<string, unknown>): Promise<ClientToolResult> {
  const sheet = typeof args.sheet === "string" && args.sheet ? args.sheet : undefined;
  const d = await Excel.getUsedRangeData(sheet);
  const fromRow = typeof args.fromRow === "number" ? Math.max(0, Math.floor(args.fromRow)) : 0;
  const shown = Math.min(d.rows.length - fromRow, MAX_EXCEL_ROWS);
  const rows = d.rows.slice(fromRow, fromRow + shown);
  return { ok: true, summary: `${sheetDataSummary(d, shown)}（${d.sheetName}）`, data: { sheetName: d.sheetName, address: d.address, headers: d.headers, rows, fromRow, totalRows: d.rows.length } };
}
async function excelListSheets(): Promise<ClientToolResult> {
  const names = await Excel.listSheets();
  return { ok: true, summary: `共 ${names.length} 个工作表`, data: { names } };
}
async function excelApplyEdits(args: Record<string, unknown>): Promise<ClientToolResult> {
  const ops = Array.isArray(args.ops) ? args.ops : null;
  if (!ops) return fail("缺少 ops 操作列表");
  const log = await Excel.applyOps(ops as never);
  return { ok: true, summary: `执行 ${log.length} 项：${log.join("；")}`, data: { log } };
}
async function excelFind(args: Record<string, unknown>): Promise<ClientToolResult> {
  const query = String(args.query ?? "");
  if (!query) return fail("缺少 query 搜索词");
  const r = await Excel.findCells(query, {
    matchCase: !!args.matchCase,
    matchEntireCell: !!args.matchEntireCell,
    sheet: typeof args.sheet === "string" ? args.sheet : undefined,
    maxResults: typeof args.maxResults === "number" ? args.maxResults : undefined,
  });
  const shown = r.hits.length;
  const summary =
    r.total === 0
      ? `未找到包含「${query}」的单元格`
      : shown >= r.total
      ? `找到 ${r.total} 个单元格`
      : `共找到 ${r.total} 个，本次返回前 ${shown} 个`;
  return { ok: true, summary, data: { query, hits: r.hits, total: r.total, truncated: shown < r.total } };
}
async function excelProfile(args: Record<string, unknown>): Promise<ClientToolResult> {
  const r = await Excel.profileSheet(typeof args.sheet === "string" ? args.sheet : undefined);
  const numericCols = r.columns.filter((c) => c.kind === "number").length;
  const missingCols = r.columns.filter((c) => c.missing > 0).length;
  const summary = `${r.rowCount} 行 × ${r.columns.length} 列（数值列 ${numericCols}，有缺失值的列 ${missingCols}）`;
  return { ok: true, summary, data: r };
}
async function excelGroupBy(args: Record<string, unknown>): Promise<ClientToolResult> {
  const keyCol = typeof args.keyColumnIndex === "number" ? Math.floor(args.keyColumnIndex) : 0;
  const valueCols = Array.isArray(args.valueColumnIndexes)
    ? (args.valueColumnIndexes as unknown[]).map((n) => Math.floor(Number(n)))
    : [];
  type Op = "sum" | "mean" | "count" | "min" | "max";
  const ALLOWED: Op[] = ["sum", "mean", "count", "min", "max"];
  const ops: Op[] = Array.isArray(args.ops)
    ? (args.ops as unknown[]).map((o) => String(o) as Op).filter((o) => ALLOWED.includes(o))
    : ["sum"];
  if (!valueCols.length && !ops.includes("count")) return fail("valueColumnIndexes 为空时只能用 count 聚合");
  const r = await Excel.groupBy(keyCol, valueCols, ops, {
    sheet: typeof args.sheet === "string" ? args.sheet : undefined,
  });
  const summary = `按「${r.keyColumn.label || r.keyColumn.letter}」分组，共 ${r.aggregations.length} 组`;
  return { ok: true, summary, data: r };
}
async function excelFindDuplicates(args: Record<string, unknown>): Promise<ClientToolResult> {
  const keyCols = Array.isArray(args.keyColumnIndexes)
    ? (args.keyColumnIndexes as unknown[]).map((n) => Math.floor(Number(n)))
    : [];
  const r = await Excel.findDuplicates(keyCols, {
    sheet: typeof args.sheet === "string" ? args.sheet : undefined,
    maxGroups: typeof args.maxGroups === "number" ? args.maxGroups : undefined,
  });
  const summary = r.totalDuplicateRows === 0
    ? `没有重复行`
    : `发现 ${r.groups.length} 组重复，共涉及 ${r.totalDuplicateRows} 行`;
  return { ok: true, summary, data: r };
}
async function excelWriteSummary(args: Record<string, unknown>): Promise<ClientToolResult> {
  const title = String(args.title ?? "AI 分析").trim();
  const paragraphs = asStringArray(args.paragraphs);
  if (!paragraphs.length) return fail("缺少摘要段落");
  const name = await Excel.writeSummary(title, paragraphs);
  return { ok: true, summary: `已写入「${name}」（${paragraphs.length} 段）` };
}
async function excelAddChart(args: Record<string, unknown>): Promise<ClientToolResult> {
  const title = String(args.title ?? "图表").trim();
  const type = String(args.type ?? "column") as ChartType;
  const table = asTable(args.table);
  if (!table || table.length < 2) return fail("table 至少需 2 行（表头+数据）");
  const name = await Excel.chartFromTable(title, type, table);
  return { ok: true, summary: `已生成图表「${title}」于「${name}」` };
}
async function excelAddPivot(args: Record<string, unknown>): Promise<ClientToolResult> {
  const table = asTable(args.table);
  if (!table || table.length < 2) return fail("table 至少需 2 行");
  const rowIndexes = (Array.isArray(args.rowIndexes) ? args.rowIndexes : []).map((n) => Number(n));
  const colIndexes = (Array.isArray(args.colIndexes) ? args.colIndexes : []).map((n) => Number(n));
  const valueIndexes = (Array.isArray(args.valueIndexes) ? args.valueIndexes : []).map((n) => Number(n));
  if (!rowIndexes.length || !valueIndexes.length) return fail("透视表至少需要 1 个行字段和 1 个值字段");
  if (!Excel.supportsPivot()) return fail("当前 Excel 版本不支持透视表（需 ExcelApi 1.8+）");
  const name = await Excel.pivotFromTable(table, rowIndexes, colIndexes, valueIndexes);
  return { ok: true, summary: `已生成透视表于「${name}」` };
}

// ---- PowerPoint ----
async function pptGetSelection(): Promise<ClientToolResult> {
  const text = await Ppt.getSelectedText();
  return { ok: true, summary: text.trim() ? `读取到选区（${text.length} 字）` : "选区为空", data: { text } };
}
async function pptGetAllSlides(): Promise<ClientToolResult> {
  const slides = await Ppt.getAllSlidesText();
  const current = slides.find((s) => s.current)?.slide ?? null;
  const summary = current
    ? `读取到 ${slides.length} 页，当前正在查看第 ${current} 页`
    : `读取到 ${slides.length} 页`;
  return { ok: true, summary, data: { slides, currentSlide: current, total: slides.length } };
}
async function pptGetCurrentSlide(): Promise<ClientToolResult> {
  const idx = await Ppt.getCurrentSlideIndex();
  if (idx === null) return { ok: false, summary: "无法获取当前页（Office 版本过旧或未选中幻灯片）" };
  // Also fetch that slide's text in one shot so the agent has full context.
  const all = await Ppt.getAllSlidesText();
  const entry = all.find((s) => s.slide === idx);
  return {
    ok: true,
    summary: entry ? `当前第 ${idx} 页（共 ${all.length} 页）` : `当前第 ${idx} 页`,
    data: { currentSlide: idx, total: all.length, texts: entry?.texts ?? [], slide: idx },
  };
}
async function pptApplyEdits(args: Record<string, unknown>): Promise<ClientToolResult> {
  const ops = Array.isArray(args.ops) ? args.ops : null;
  if (!ops) return fail("缺少 ops 操作列表");
  const log = await Ppt.applyOps(ops as never);
  return { ok: true, summary: `执行 ${log.length} 项：${log.join("；")}`, data: { log } };
}

export interface ClientToolDef {
  label: string;
  fn: (args: Record<string, unknown>) => Promise<ClientToolResult>;
}

const TOOLS: Record<string, ClientToolDef> = {
  word_get_selection: { label: "读取 Word 选区", fn: () => wordGetSelection() },
  word_get_document: { label: "读取 Word 全文", fn: () => wordGetDocument() },
  word_apply_edits: { label: "编辑 Word 文档", fn: wordApplyEdits },
  word_add_comment: { label: "添加 Word 批注", fn: wordAddComment },
  word_search: { label: "搜索 Word 内容", fn: wordSearch },
  word_highlight: { label: "批量高亮 Word 关键词", fn: wordHighlight },
  word_add_comment_at: { label: "批量添加 Word 批注", fn: wordAddCommentAt },
  word_get_highlights: { label: "读取 Word 已高亮内容", fn: wordGetHighlights },
  word_clear_highlights: { label: "清除 Word 高亮", fn: wordClearHighlights },
  word_get_outline: { label: "读取 Word 大纲", fn: () => wordGetOutline() },
  word_replace_all: { label: "Word 查找替换", fn: wordReplaceAll },
  excel_get_selection: { label: "读取 Excel 选区", fn: () => excelGetSelection() },
  excel_get_sheet: { label: "读取 Excel 工作表", fn: excelGetSheet },
  excel_list_sheets: { label: "列出 Excel 工作表", fn: () => excelListSheets() },
  excel_apply_edits: { label: "编辑 Excel", fn: excelApplyEdits },
  excel_write_summary: { label: "写入 Excel 摘要", fn: excelWriteSummary },
  excel_add_chart: { label: "生成 Excel 图表", fn: excelAddChart },
  excel_add_pivot: { label: "生成 Excel 透视表", fn: excelAddPivot },
  excel_find: { label: "查找 Excel 单元格", fn: excelFind },
  excel_profile: { label: "Excel 列画像", fn: excelProfile },
  excel_group_by: { label: "Excel 分组聚合", fn: excelGroupBy },
  excel_find_duplicates: { label: "Excel 查找重复行", fn: excelFindDuplicates },
  ppt_get_selection: { label: "读取 PPT 选区", fn: () => pptGetSelection() },
  ppt_get_all_slides: { label: "读取 PPT 全部页", fn: () => pptGetAllSlides() },
  ppt_get_current_slide: { label: "读取 PPT 当前页", fn: () => pptGetCurrentSlide() },
  ppt_apply_edits: { label: "编辑 PPT", fn: pptApplyEdits },
};

/** Lazy map of tool name → label, derived from TOOLS. */
export const TOOL_LABELS: Record<string, string> = {
  web_search: "联网搜索", // server-side tool, not in TOOLS
};
for (const [name, def] of Object.entries(TOOLS)) {
  TOOL_LABELS[name] = def.label;
}

/** Execute one client tool call. Never throws — failures return { ok:false }. */
export async function executeClientTool(name: string, args: Record<string, unknown>): Promise<ClientToolResult> {
  const def = TOOLS[name];
  if (!def) return fail(`未知客户端工具：${name}`);
  try {
    return await def.fn(args || {});
  } catch (e) {
    return fail(String((e as Error)?.message || e));
  }
}

/** Whether a tool name is valid for the given host (used for sanity checks). */
export function isKnownClientTool(name: string, host: HostKind): boolean {
  const prefix = host === "word" ? "word_" : host === "excel" ? "excel_" : host === "powerpoint" ? "ppt_" : null;
  return !!prefix && name.startsWith(prefix) && !!TOOLS[name];
}
